/**
 * Private send via HoudiniSwap (PRIVATE-TRANSFER-001): the funds go through a
 * one-time deposit address of the provider, so the recipient never sees the
 * sender's wallet. State machine of the backend row (`private-exchange-status.enum.ts`):
 *
 * `CREATED → ORDER_CREATED → FUNDING → DEPOSITED → PROCESSING → COMPLETED`,
 * `REFUNDED` / `ATTENTION_REQUIRED` from any step ≥ `ORDER_CREATED`,
 * `CANCELLED` only up to `FUNDING`, `CANCELLED → ATTENTION_REQUIRED → RESOLVED`.
 */
export const PRIVATE_TRANSFER_STATUSES = [
  'CREATED',
  'ORDER_CREATED',
  'FUNDING',
  'DEPOSITED',
  'PROCESSING',
  'COMPLETED',
  'REFUNDED',
  'CANCELLED',
  'ATTENTION_REQUIRED',
  'RESOLVED',
] as const;

export type PrivateTransferStatus = (typeof PRIVATE_TRANSFER_STATUSES)[number] | 'UNKNOWN';

/** `tokenAddress` of a chain's gas token in `GET /private-transfers/tokens`. */
export const NATIVE_TOKEN_ADDRESS = 'native';

/** Default of the backend's `PRIVATE_EXCHANGE_MAX_ACTIVE_PER_USER`. */
export const MAX_ACTIVE_TRANSFERS = 3;

/** Nominal lifetime of a quote handle (`HOUDINI_QUOTE_TTL_SEC`). */
export const QUOTE_TTL_HINT_MS = 60_000;

/**
 * `POST /:id/funding-intent` is refused when less than this is left until the
 * provider order expires — a new order (a new quote) is needed instead.
 */
export const REQUOTE_THRESHOLD_MS = 10 * 60_000;

export interface PrivateTransferToken {
  readonly chainId: string;
  /** Canonical address, or `'native'` for the gas token. */
  readonly tokenAddress: string;
  /** On-chain exponent — the only divisor for this token's raw amounts. */
  readonly decimals: number;
  /** `POST /quote` rounds `amountRaw` down to `10^(decimals − amountPrecision)`. */
  readonly amountPrecision: number;
  readonly symbol: string | null;
  /** `null` when the provider bounds are unavailable — no amount can be accepted then. */
  readonly minRaw: bigint | null;
  readonly maxRaw: bigint | null;
}

export interface PrivateTransferQuote {
  /** Opaque, single-use handle for `POST /private-transfers`. */
  readonly quoteRef: string;
  /** Quantised down — what will actually be signed, not what was typed. */
  readonly amountRaw: bigint;
  readonly expectedToAmountRaw: bigint;
  /** Display only: `inUsd − outUsd`. */
  readonly feeUsd: string;
  readonly etaMinutes: number;
  /** ISO time the quote handle dies (~60 s after issue). */
  readonly validUntil: string;
}

export interface PrivateTransfer {
  readonly id: string;
  readonly status: PrivateTransferStatus;
  readonly chainId: string;
  readonly fromAmountRaw: bigint;
  /** Expected payout; the resource carries no actual payout amount. */
  readonly expectedToAmountRaw: bigint;
  readonly recipientAddress: string;
  readonly refundAddress: string;
  readonly depositAddress: string | null;
  readonly orderExpiresAt: string | null;
  readonly providerOrderId: string | null;
  /** Funding intent to sign via `/intents/:id/sign` → `/submit`. */
  readonly fundingIntentId: string | null;
  /** Stable code — branch on it, never show it. */
  readonly failureReason: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface Paginated<T> {
  readonly data: T[];
  readonly count: number;
}

/** Visible steps of the progress timeline; `CREATED` folds into the first one. */
export const TRANSFER_STEPS: readonly PrivateTransferStatus[] = [
  'ORDER_CREATED',
  'FUNDING',
  'DEPOSITED',
  'PROCESSING',
  'COMPLETED',
];

/**
 * Timeline position. The progress screen draws the timeline only for in-flight
 * rows and `COMPLETED`; outcomes off the main line map to the step they
 * usually branch at — `CANCELLED` never gets past funding, a refund and a
 * manual review happen on the provider side.
 */
export function transferStepIndex(status: PrivateTransferStatus): number {
  switch (status) {
    case 'FUNDING':
    case 'CANCELLED':
      return 1;
    case 'DEPOSITED':
      return 2;
    case 'PROCESSING':
    case 'REFUNDED':
    case 'ATTENTION_REQUIRED':
    case 'RESOLVED':
      return 3;
    case 'COMPLETED':
      return 4;
    default:
      return 0;
  }
}

/**
 * Monotonic rank: a late poll must never rewind a status a WS event (or the
 * optimistic `FUNDING` after signing) already advanced. Every legal backend
 * transition is strictly increasing, including the one arc out of a terminal
 * state (`CANCELLED → ATTENTION_REQUIRED`). The three final outcomes share the
 * top rank: nothing follows them, and one must never replace another.
 */
export const TRANSFER_ORDER: Record<PrivateTransferStatus, number> = {
  UNKNOWN: 0,
  CREATED: 1,
  ORDER_CREATED: 2,
  FUNDING: 3,
  CANCELLED: 4,
  DEPOSITED: 5,
  PROCESSING: 6,
  ATTENTION_REQUIRED: 7,
  COMPLETED: 8,
  REFUNDED: 8,
  RESOLVED: 8,
};

/** Strictly greater: equal ranks are rival outcomes, and swapping them is the rewind this guards. */
export function advanceTransferStatus(
  current: PrivateTransferStatus,
  next: PrivateTransferStatus,
): PrivateTransferStatus {
  return TRANSFER_ORDER[next] > TRANSFER_ORDER[current] ? next : current;
}

/** Statuses the client polls: money is on its way and the row will move by itself. */
export const IN_FLIGHT_STATUSES: readonly PrivateTransferStatus[] = [
  'CREATED',
  'ORDER_CREATED',
  'FUNDING',
  'DEPOSITED',
  'PROCESSING',
];

/**
 * What the backend counts toward the per-user cap. A row under manual review
 * blocks new sends too — only an operator can release it.
 */
export const ACTIVE_STATUSES: readonly PrivateTransferStatus[] = [
  ...IN_FLIGHT_STATUSES,
  'ATTENTION_REQUIRED',
];

export function isTransferInFlight(status: PrivateTransferStatus): boolean {
  return IN_FLIGHT_STATUSES.includes(status);
}

export function isTransferActive(status: PrivateTransferStatus): boolean {
  return ACTIVE_STATUSES.includes(status);
}

/** The order exists and waits for its funding transaction to be signed. */
export function awaitsSignature(transfer: PrivateTransfer): boolean {
  return transfer.status === 'ORDER_CREATED';
}

/** Epoch ms of an ISO time; 0 when absent or unparsable. */
export function isoToMs(value: string | null): number {
  if (!value) return 0;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : 0;
}

/** Seconds until a local epoch-ms deadline; 0 when there is none or it has passed. */
export function secondsUntil(deadlineMs: number, now = Date.now()): number {
  return deadlineMs === 0 ? 0 : Math.max(0, Math.ceil((deadlineMs - now) / 1000));
}

/**
 * Local deadline of a quote, anchored to the moment its response arrived.
 * `validUntil` is server time; compared with a device clock running a minute
 * ahead it makes every quote dead on arrival, and the user could never send.
 * The server's remaining time is trusted up to the nominal TTL (a clock that
 * lags must not promise more than the quote has); a remainder that is already
 * gone can only be clock skew, and gets the nominal TTL. The authority stays
 * the backend's `409 QUOTE_EXPIRED`, which the flow handles.
 */
export function quoteDeadlineMs(validUntil: string, receivedAt = Date.now()): number {
  const ttl = isoToMs(validUntil) - receivedAt;
  return receivedAt + (ttl > 0 ? Math.min(ttl, QUOTE_TTL_HINT_MS) : QUOTE_TTL_HINT_MS);
}

/**
 * Too little of the provider order is left to refresh its funding intent.
 * An order without a known expiry is given the benefit of the doubt — the
 * backend decides with a 409 then.
 */
export function orderExpiring(transfer: PrivateTransfer, now = Date.now()): boolean {
  const expiresAt = isoToMs(transfer.orderExpiresAt);
  return expiresAt !== 0 && expiresAt - now < REQUOTE_THRESHOLD_MS;
}

/**
 * Exchange haircut `(1 − out / in) × 100`, one decimal, rounded half up —
 * bigint all the way, no floating point near money. `"4.3"`, `"2"`; `"0"` for
 * an empty or inverted pair.
 */
export function haircutPct(fromRaw: bigint, toRaw: bigint): string {
  if (fromRaw <= 0n || toRaw >= fromRaw) return '0';
  const tenths = ((fromRaw - toRaw) * 1000n + fromRaw / 2n) / fromRaw;
  const whole = tenths / 10n;
  const fraction = tenths % 10n;
  return fraction === 0n ? whole.toString() : `${whole}.${fraction}`;
}

/** What `POST /quote` will do to `amountRaw`: round it down to the token's quote step. */
export function quantizeAmount(amountRaw: bigint, token: PrivateTransferToken): bigint {
  const exponent = token.decimals - token.amountPrecision;
  if (exponent <= 0) return amountRaw;
  const step = 10n ** BigInt(exponent);
  return (amountRaw / step) * step;
}
